import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../styles/voice-sales.css";
import TableMobile from "../responsive-fixes/TableMobile";
import { useResponsiveLayout } from "../responsive-fixes/useResponsiveLayout";

const BASE = "/api/voice-sales";

// -- Helpers --------------------------------------------------------

const fmt = (n) => (n ?? 0).toLocaleString();
const pct = (n) => `${(n ?? 0).toFixed(1)}%`;

function StageBadge({ stage }) {
  const map = {
    new_lead: "new_lead",
    contacted: "contacted",
    qualified: "qualified",
    demo_scheduled: "demo_scheduled",
    demo_done: "booked",
    closed_won: "confirmed",
    closed_lost: "closed_lost",
  };
  const cls = map[stage] || "cold";
  return <span className={`vs-badge ${cls}`}>{stage?.replace(/_/g, " ")}</span>;
}

function TempBadge({ temp }) {
  const map = { hot: "hot", warm: "warm", cold: "cold" };
  return <span className={`vs-badge ${map[temp] || "cold"}`}>{temp || "--"}</span>;
}

function Spinner() {
  return (
    <div className="vs-loading">
      <div className="vs-spinner" /> Loading...
    </div>
  );
}

// -- Tab: Overview --------------------------------------------------

function OverviewTab({ stats, onRefresh }) {
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);

  const loadBriefing = async () => {
    setBriefingLoading(true);
    try {
      const r = await fetch(`${BASE}/briefing/daily`);
      const d = await r.json();
      setBriefing(d.briefing);
    } catch (e) {
      setBriefing("Unable to load briefing.");
    } finally {
      setBriefingLoading(false);
    }
  };

  if (!stats) return <Spinner />;

  const pipeline = stats.pipeline || {};
  const kpis = [
    { label: "Total Leads", value: fmt(stats.total_leads), color: "" },
    { label: "Called Today", value: fmt(stats.called_today), color: "" },
    { label: "Connected", value: fmt(stats.connected_today), color: "green", meta: pct(stats.connect_rate) + " rate" },
    { label: "Qualified", value: fmt(stats.qualified_today), color: "green" },
    { label: "Demos Today", value: fmt(stats.demos_booked_today), color: stats.demos_booked_today > 0 ? "green" : "amber" },
    { label: "Demos This Week", value: fmt(stats.demos_booked_week), color: "green" },
    { label: "Upcoming Demos", value: fmt(stats.upcoming_demos?.length ?? 0), color: "amber" },
  ];

  const rates = [
    { label: "Connect Rate", value: stats.connect_rate ?? 0, cls: "primary" },
    { label: "Qualification Rate", value: stats.qualification_rate ?? 0, cls: "green" },
    { label: "Demo Booking Rate", value: stats.booking_rate ?? 0, cls: "amber" },
  ];

  const stageLabels = {
    new_lead: "New",
    contacted: "Contacted",
    qualified: "Qualified",
    demo_scheduled: "Demo Sched.",
    demo_done: "Demo Done",
    closed_won: "Won",
    closed_lost: "Lost",
  };

  return (
    <div>
      <div className="vs-kpi-row">
        {kpis.map((k, i) => (
          <div key={i} className={`vs-kpi ${k.color}`}>
            <div className="vs-kpi-label">{k.label}</div>
            <div className="vs-kpi-value">{k.value}</div>
            {k.meta && <div className="vs-kpi-meta">{k.meta}</div>}
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-md)", marginBottom: "var(--space-md)" }}>
        <div className="vs-card">
          <div className="vs-card-title">Pipeline</div>
          <div className="vs-pipeline">
            {Object.entries(stageLabels).map(([key, label]) => (
              <div key={key} className="vs-funnel-stage">
                <div className="vs-funnel-count">{pipeline[key] ?? 0}</div>
                <div className="vs-funnel-label">{label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="vs-card">
          <div className="vs-card-title">Conversion Rates</div>
          <div className="vs-rates">
            {rates.map((r) => (
              <div key={r.label} className="vs-rate-row">
                <div className="vs-rate-label">{r.label}</div>
                <div className="vs-rate-bar-bg">
                  <div className={`vs-rate-bar ${r.cls}`} style={{ width: `${Math.min(r.value, 100)}%` }} />
                </div>
                <div className="vs-rate-pct">{pct(r.value)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="vs-card">
        <div className="vs-card-title" style={{ justifyContent: "space-between" }}>
          <span>Daily Briefing</span>
          <button className="vs-btn vs-btn-ghost vs-btn-sm" onClick={loadBriefing} disabled={briefingLoading}>
            {briefingLoading ? "Loading..." : "Generate"}
          </button>
        </div>
        {briefing ? (
          <div className="vs-briefing">{briefing}</div>
        ) : (
          <div className="vs-empty" style={{ padding: "var(--space-md)" }}>
            <div style={{ color: "var(--color-muted)", fontSize: "var(--font-size-sm)" }}>
              Click Generate to get an AI morning briefing for your sales team.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// -- Tab: Leads -----------------------------------------------------

function LeadsTab() {
  const responsive = useResponsiveLayout();
  const useMobileTable = responsive.isPhone || responsive.isPhablet;
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importJson, setImportJson] = useState("");
  const [territory, setTerritory] = useState("UAE");
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [discoverQuery, setDiscoverQuery] = useState("hair salon");
  const [discoverCity, setDiscoverCity] = useState("Dubai");
  const [discoverLimit, setDiscoverLimit] = useState(10);
  const [discovering, setDiscovering] = useState(false);
  const [discoverMsg, setDiscoverMsg] = useState("");

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const url = stageFilter ? `${BASE}/leads?stage=${stageFilter}` : `${BASE}/leads`;
      const r = await fetch(url);
      const d = await r.json();
      setLeads(d.leads || []);
    } catch (e) {
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [stageFilter]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const handleImport = async () => {
    setImportMsg("");
    let parsed;
    try {
      parsed = JSON.parse(importJson);
    } catch (e) {
      setImportMsg("Invalid JSON");
      return;
    }
    const arr = Array.isArray(parsed) ? parsed : [parsed];
    setImporting(true);
    try {
      const r = await fetch(`${BASE}/leads/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leads: arr, territory }),
      });
      const d = await r.json();
      if (d.success) {
        setImportMsg(`Imported ${d.imported} lead(s)`);
        setImportJson("");
        loadLeads();
      } else {
        setImportMsg(`${d.detail || "Import failed"}`);
      }
    } catch (e) {
      setImportMsg("Network error");
    } finally {
      setImporting(false);
    }
  };

  const runDiscover = async () => {
    setDiscoverMsg("");
    setDiscovering(true);
    try {
      const r = await fetch(`${BASE}/leads/discover`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: discoverQuery,
          city: discoverCity,
          limit: Number(discoverLimit) || 10,
          save_to_crm: true,
        }),
      });
      const d = await r.json();
      if (d.success) {
        setDiscoverMsg(
          `Found ${d.total_found} leads via ${d.source}. ${d.crm_created} added to CRM.`
        );
        loadLeads();
      } else {
        setDiscoverMsg(`${d.error || "Discovery failed"}`);
      }
    } catch (e) {
      setDiscoverMsg("Network error");
    } finally {
      setDiscovering(false);
    }
  };

  const stages = ["", "new_lead", "contacted", "qualified", "demo_scheduled", "demo_done", "closed_won", "closed_lost"];

  return (
    <div>
      <div className="vs-card">
        <div className="vs-card-title">Discover Leads (Internet Search)</div>
        <div className="vs-import-row">
          <input
            className="vs-call-input"
            placeholder="Search query (e.g. hair salon, beauty centre)"
            value={discoverQuery}
            onChange={(e) => setDiscoverQuery(e.target.value)}
            style={{ flex: "2", minWidth: "200px", padding: "8px 12px", background: "var(--color-surface-alt)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", color: "var(--color-light)", fontFamily: "var(--font-family)", fontSize: "var(--font-size-sm)", outline: "none" }}
          />
          <select className="vs-select" value={discoverCity} onChange={(e) => setDiscoverCity(e.target.value)}>
            {["Dubai", "Abu Dhabi", "Sharjah", "Muscat", "Riyadh", "Jeddah", "Kuwait City", "Doha", "Manama"].map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <input
            className="vs-call-input"
            type="number"
            min="1"
            max="50"
            value={discoverLimit}
            onChange={(e) => setDiscoverLimit(e.target.value)}
            style={{ width: "80px", padding: "8px 12px", background: "var(--color-surface-alt)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", color: "var(--color-light)", fontFamily: "var(--font-family)", fontSize: "var(--font-size-sm)", outline: "none" }}
          />
          <button className="vs-btn vs-btn-primary" onClick={runDiscover} disabled={discovering || !discoverQuery.trim()}>
            {discovering ? "Searching..." : "Discover"}
          </button>
          {discoverMsg && <span style={{ fontSize: "var(--font-size-sm)", color: discoverMsg.startsWith("Found") ? "var(--color-accent)" : "var(--color-error)" }}>{discoverMsg}</span>}
        </div>
      </div>

      <div className="vs-card">
        <div className="vs-card-title">Import Leads</div>
        <div className="vs-import">
          <textarea
            className="vs-textarea"
            placeholder={'Paste JSON array of leads:\n[\n  {"salon_name":"Glow Salon","phone":"+971501234567","city":"Dubai","country":"UAE"},\n  ...\n]'}
            value={importJson}
            onChange={(e) => setImportJson(e.target.value)}
          />
          <div className="vs-import-row">
            <select className="vs-select" value={territory} onChange={(e) => setTerritory(e.target.value)}>
              {["UAE", "KSA", "Kuwait", "Bahrain", "Qatar", "Oman"].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <button className="vs-btn vs-btn-primary" onClick={handleImport} disabled={importing || !importJson.trim()}>
              {importing ? "Importing..." : "Import Leads"}
            </button>
            {importMsg && <span style={{ fontSize: "var(--font-size-sm)", color: importMsg.startsWith("Imported") ? "var(--color-accent)" : "var(--color-error)" }}>{importMsg}</span>}
          </div>
        </div>
      </div>

      <div className="vs-card">
        <div className="vs-card-title" style={{ justifyContent: "space-between" }}>
          <span>Leads ({leads.length})</span>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <select className="vs-select" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
              <option value="">All Stages</option>
              {stages.slice(1).map((s) => (
                <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
              ))}
            </select>
            <button className="vs-btn vs-btn-ghost vs-btn-sm" onClick={loadLeads}>Refresh</button>
          </div>
        </div>

        {loading ? <Spinner /> : leads.length === 0 ? (
          <div className="vs-empty">
            <div className="vs-empty-icon">No leads yet</div>
            <div>Import some above.</div>
          </div>
        ) : useMobileTable ? (
          // M3 fix (audit): leads table strip-clipped below md.
          <TableMobile
            columns={[
              { key: "salon_name", label: "Salon", primary: true,
                format: (v) => v || "--",
              },
              { key: "lead_temperature", label: "Temp", status: true,
                format: (v) => v || "—",
              },
              { key: "contact_name", label: "Contact", format: (v) => v || "--" },
              { key: "phone", label: "Phone" },
              { key: "city", label: "City", format: (v) => v || "--" },
              { key: "pipeline_stage", label: "Stage" },
              { key: "call_attempts", label: "Attempts", format: (v) => v ?? 0 },
            ]}
            rows={leads}
            statusTone={(row) => {
              const t = String(row.lead_temperature || "").toLowerCase();
              if (t === "hot") return "danger";
              if (t === "warm") return "warning";
              if (t === "cold") return "info";
              return "muted";
            }}
          />
        ) : (
          <div className="vs-table-wrap">
            <table className="vs-table">
              <thead>
                <tr>
                  <th>Salon</th>
                  <th>Contact</th>
                  <th>Phone</th>
                  <th>City</th>
                  <th>Stage</th>
                  <th>Temp</th>
                  <th>Attempts</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id}>
                    <td>{l.salon_name || "--"}</td>
                    <td>{l.contact_name || <span className="muted">--</span>}</td>
                    <td className="muted">{l.phone}</td>
                    <td className="muted">{l.city || "--"}</td>
                    <td><StageBadge stage={l.pipeline_stage} /></td>
                    <td><TempBadge temp={l.lead_temperature} /></td>
                    <td className="muted">{l.call_attempts ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// -- Tab: Call Simulator ---------------------------------------------

function CallTab() {
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState("");
  const [language, setLanguage] = useState("en");
  const [callId, setCallId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [calling, setCalling] = useState(false);
  const [sending, setSending] = useState(false);
  const [playingIdx, setPlayingIdx] = useState(null);
  const convRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    fetch(`${BASE}/leads`)
      .then((r) => r.json())
      .then((d) => setLeads(d.leads || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (convRef.current) convRef.current.scrollTop = convRef.current.scrollHeight;
  }, [messages]);

  const addMsg = (role, text) =>
    setMessages((m) => [...m, { role, text, ts: new Date().toLocaleTimeString() }]);

  const startCall = async () => {
    if (!selectedLead) return;
    setCalling(true);
    setMessages([]);
    setCallId(null);
    try {
      const r = await fetch(`${BASE}/call/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead_id: selectedLead, language, force: true }),
      });
      const d = await r.json();
      if (d.call_id) {
        setCallId(d.call_id);
        addMsg("system", `Call started -- ID: ${d.call_id}`);
        if (d.script) addMsg("agent", d.script.text || d.script);
      } else {
        addMsg("system", `Error: ${d.detail || d.error || "Could not start call"}`);
      }
    } catch (e) {
      addMsg("system", "Network error starting call");
    } finally {
      setCalling(false);
    }
  };

  const sendTurn = async (text) => {
    if (!callId || !text.trim()) return;
    addMsg("customer", text);
    setInput("");
    setSending(true);
    try {
      const r = await fetch(`${BASE}/call/turn`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ call_id: callId, customer_speech: text }),
      });
      const d = await r.json();
      if (d.script) addMsg("agent", d.script.text || JSON.stringify(d.script));
      if (d.current_state) addMsg("system", `State: ${d.current_state}`);
      if (d.intent_detected) addMsg("system", `Intent: ${d.intent_detected}`);
    } catch (e) {
      addMsg("system", "Network error");
    } finally {
      setSending(false);
    }
  };

  const playAudio = async (text, idx) => {
    if (playingIdx === idx) {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      setPlayingIdx(null);
      return;
    }
    setPlayingIdx(idx);
    try {
      const r = await fetch(`${BASE}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, language }),
      });
      if (!r.ok) { setPlayingIdx(null); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      if (audioRef.current) { audioRef.current.pause(); }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setPlayingIdx(null); URL.revokeObjectURL(url); };
      audio.onerror = () => { setPlayingIdx(null); };
      audio.play();
    } catch (e) {
      setPlayingIdx(null);
    }
  };

  const quickIntents = ["interested", "not interested", "busy", "callback request", "book demo", "pricing question"];

  return (
    <div className="vs-call-panel">
      <div className="vs-card">
        <div className="vs-card-title">Call Simulator</div>
        <div className="vs-call-select-row">
          <select
            className="vs-select vs-call-input"
            value={selectedLead}
            onChange={(e) => setSelectedLead(e.target.value)}
          >
            <option value="">-- Select a lead --</option>
            {leads.map((l) => (
              <option key={l.id} value={l.id}>
                {l.salon_name || l.contact_name || l.phone} -- {l.city}
              </option>
            ))}
          </select>
          <select className="vs-select" value={language} onChange={(e) => setLanguage(e.target.value)}>
            <option value="en">English</option>
            <option value="ar">Arabic</option>
          </select>
          <button
            className="vs-btn vs-btn-primary"
            onClick={startCall}
            disabled={calling || !selectedLead}
          >
            {calling ? "Connecting..." : callId ? "Restart" : "Start Call"}
          </button>
        </div>
      </div>

      <div className="vs-card" style={{ padding: 0, overflow: "hidden" }}>
        <div ref={convRef} className="vs-conversation" style={{ maxHeight: 380, margin: 0, borderRadius: "var(--radius-lg)" }}>
          {messages.length === 0 ? (
            <div className="vs-bubble system">Select a lead and start a call simulation.</div>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={`vs-bubble ${m.role}`}>
                {m.role !== "system" && (
                  <div className="vs-bubble-role">{m.role === "agent" ? "Sarah" : "Customer"} . {m.ts}</div>
                )}
                {m.text}
                {m.role === "agent" && (
                  <div>
                    <button
                      className={`vs-audio-btn ${playingIdx === i ? "playing" : ""}`}
                      onClick={() => playAudio(m.text, i)}
                    >
                      {playingIdx === i ? "Playing" : "Play"}
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {callId && (
        <div className="vs-card">
          <div className="vs-response-row">
            <input
              className="vs-response-input"
              placeholder="Type customer response..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendTurn(input); } }}
              disabled={sending}
            />
            <button
              className="vs-btn vs-btn-primary"
              onClick={() => sendTurn(input)}
              disabled={sending || !input.trim()}
            >
              {sending ? "..." : "Send"}
            </button>
          </div>
          <div className="vs-quick-intents">
            {quickIntents.map((intent) => (
              <button
                key={intent}
                className="vs-intent-btn"
                onClick={() => sendTurn(intent)}
                disabled={sending}
              >
                {intent}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// -- Tab: Demos -----------------------------------------------------

function DemosTab() {
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("upcoming");

  const loadDemos = useCallback(async () => {
    setLoading(true);
    try {
      const url = view === "upcoming" ? `${BASE}/demos/upcoming` : `${BASE}/demos`;
      const r = await fetch(url);
      const d = await r.json();
      setDemos(d.demos || []);
    } catch (e) {
      setDemos([]);
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { loadDemos(); }, [loadDemos]);

  const sendReminders = async () => {
    try {
      const r = await fetch(`${BASE}/demo/reminders`, { method: "POST" });
      const d = await r.json();
      alert(`Reminders sent: ${JSON.stringify(d.reminders_sent)}`);
    } catch (e) { alert("Error sending reminders"); }
  };

  const fmtDate = (dt) => {
    if (!dt) return "--";
    const d = new Date(dt);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div>
      <div className="vs-card">
        <div className="vs-card-title" style={{ justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: "6px" }}>
            {["upcoming", "all"].map((v) => (
              <button
                key={v}
                className={`vs-btn vs-btn-ghost vs-btn-sm ${view === v ? "active" : ""}`}
                style={view === v ? { background: "var(--color-primary-light)", color: "var(--color-primary)" } : {}}
                onClick={() => setView(v)}
              >
                {v === "upcoming" ? "Upcoming" : "All Demos"}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button className="vs-btn vs-btn-ghost vs-btn-sm" onClick={sendReminders}>Send Reminders</button>
            <button className="vs-btn vs-btn-ghost vs-btn-sm" onClick={loadDemos}>Refresh</button>
          </div>
        </div>

        {loading ? <Spinner /> : demos.length === 0 ? (
          <div className="vs-empty">
            <div className="vs-empty-icon">No demos</div>
            <div>No demos {view === "upcoming" ? "upcoming" : "found"}.</div>
          </div>
        ) : (
          <div className="vs-demo-list">
            {demos.map((d) => (
              <div key={d.id} className="vs-demo-row">
                <div className="vs-demo-date">{fmtDate(d.scheduled_datetime)}</div>
                <div className="vs-demo-salon">{d.salon_name || d.lead_id}</div>
                <div className="vs-demo-meta">{d.format?.replace(/_/g, " ") || "In Salon"}</div>
                <div className="vs-demo-meta">{d.location_address || ""}</div>
                <StageBadge stage={d.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// -- Tab: Scripts ---------------------------------------------------

function ScriptsTab() {
  const [states, setStates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [lang, setLang] = useState("en");
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetch(`${BASE}/scripts`)
      .then((r) => r.json())
      .then((d) => {
        setStates(d.states || []);
        if (d.states?.length) setSelected(d.states[0]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    fetch(`${BASE}/scripts/${selected}?lang=${lang}`)
      .then((r) => r.json())
      .then((d) => setScript(d.script))
      .catch(() => setScript(null))
      .finally(() => setLoading(false));
  }, [selected, lang]);

  const playScript = async () => {
    if (playing) {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      setPlaying(false);
      return;
    }
    setPlaying(true);
    try {
      const r = await fetch(`${BASE}/tts/script/${selected}?lang=${lang}`);
      if (!r.ok) { setPlaying(false); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setPlaying(false); URL.revokeObjectURL(url); };
      audio.onerror = () => setPlaying(false);
      audio.play();
    } catch (e) {
      setPlaying(false);
    }
  };

  const fmtState = (s) => s?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div>
      <div className="vs-card">
        <div className="vs-script-grid">
          <div>
            <div className="vs-card-title">Script States</div>
            <div className="vs-script-list">
              {states.map((s) => (
                <div
                  key={s}
                  className={`vs-script-item ${selected === s ? "active" : ""}`}
                  onClick={() => setSelected(s)}
                >
                  {fmtState(s)}
                </div>
              ))}
            </div>
          </div>

          <div className="vs-script-detail">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
              <div className="vs-card-title" style={{ margin: 0 }}>
                {fmtState(selected) || "Select a state"}
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <div className="vs-lang-toggle">
                  {["en", "ar"].map((l) => (
                    <button
                      key={l}
                      className={`vs-lang-btn ${lang === l ? "active" : ""}`}
                      onClick={() => setLang(l)}
                    >
                      {l === "en" ? "EN" : "AR"}
                    </button>
                  ))}
                </div>
                <button
                  className={`vs-btn vs-btn-ghost vs-btn-sm ${playing ? "active" : ""}`}
                  style={playing ? { color: "var(--color-accent)", borderColor: "var(--color-accent)" } : {}}
                  onClick={playScript}
                  disabled={!selected || loading}
                >
                  {playing ? "Stop" : "Play"}
                </button>
              </div>
            </div>

            {loading ? <Spinner /> : script ? (
              <>
                <div className="vs-script-text" dir={lang === "ar" ? "rtl" : "ltr"}>
                  {script.text || "--"}
                </div>
                {script.transitions && Object.keys(script.transitions).length > 0 && (
                  <div>
                    <div style={{ fontSize: "var(--font-size-xs)", color: "var(--color-muted)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "1px" }}>
                      Transitions
                    </div>
                    <div className="vs-transitions">
                      {Object.entries(script.transitions).map(([k, v]) => (
                        <div key={k} className="vs-transition-tag">
                          if <span>{k}</span> then <span>{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : selected ? (
              <div className="vs-empty">Script not found for this state.</div>
            ) : (
              <div className="vs-empty">Select a state from the list.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// -- Tab: Compliance -------------------------------------------------

function ComplianceTab() {
  const [rules, setRules] = useState({});
  const [loading, setLoading] = useState(true);
  const [dncPhone, setDncPhone] = useState("");
  const [dncReason, setDncReason] = useState("");
  const [dncMsg, setDncMsg] = useState("");

  useEffect(() => {
    fetch(`${BASE}/compliance/rules`)
      .then((r) => r.json())
      .then((d) => setRules(d.territories || {}))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const addDNC = async () => {
    if (!dncPhone.trim()) return;
    try {
      const phone = encodeURIComponent(dncPhone.trim());
      const r = await fetch(`${BASE}/compliance/dnc/${phone}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: dncReason }),
      });
      const d = await r.json();
      if (d.success) {
        setDncMsg(`${dncPhone} added to DNC`);
        setDncPhone("");
        setDncReason("");
      } else {
        setDncMsg("Failed");
      }
    } catch (e) {
      setDncMsg("Network error");
    }
  };

  return (
    <div>
      <div className="vs-card">
        <div className="vs-card-title">Do Not Call -- Add Number</div>
        <div className="vs-import-row">
          <input
            className="vs-call-input"
            placeholder="+971501234567"
            value={dncPhone}
            onChange={(e) => setDncPhone(e.target.value)}
            style={{ flex: "1", minWidth: "180px", padding: "8px 12px", background: "var(--color-surface-alt)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", color: "var(--color-light)", fontFamily: "var(--font-family)", fontSize: "var(--font-size-sm)", outline: "none" }}
          />
          <input
            className="vs-call-input"
            placeholder="Reason (optional)"
            value={dncReason}
            onChange={(e) => setDncReason(e.target.value)}
            style={{ flex: "2", minWidth: "180px", padding: "8px 12px", background: "var(--color-surface-alt)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", color: "var(--color-light)", fontFamily: "var(--font-family)", fontSize: "var(--font-size-sm)", outline: "none" }}
          />
          <button className="vs-btn vs-btn-danger" onClick={addDNC} disabled={!dncPhone.trim()}>
            Add to DNC
          </button>
          {dncMsg && <span style={{ fontSize: "var(--font-size-sm)", color: dncMsg.endsWith("DNC") ? "var(--color-accent)" : "var(--color-error)" }}>{dncMsg}</span>}
        </div>
      </div>

      <div className="vs-card">
        <div className="vs-card-title">Territory Rules</div>
        {loading ? <Spinner /> : (
          <div className="vs-compliance-grid">
            {Object.entries(rules).map(([territory, rule]) => (
              <div key={territory} className="vs-territory-card">
                <div className="vs-territory-name">{territory}</div>
                <div className="vs-territory-row">
                  <span>Calling Window</span>
                  <span>{rule.calling_window}</span>
                </div>
                <div className="vs-territory-row">
                  <span>Max Retries / Day</span>
                  <span>{rule.max_retries_day}</span>
                </div>
                <div className="vs-territory-row">
                  <span>Max Retries / Week</span>
                  <span>{rule.max_retries_week}</span>
                </div>
                <div className="vs-territory-row">
                  <span>Block on Rejection</span>
                  <span>{rule.block_on_rejection ? "Yes" : "No"}</span>
                </div>
                {rule.dnc_register && (
                  <div className="vs-territory-row">
                    <span>DNC Register</span>
                    <span>{rule.dnc_register}</span>
                  </div>
                )}
                {rule.notes && <div className="vs-territory-note">{rule.notes}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// -- Tab: Inbound Calls ---------------------------------------------

function InboundTab() {
  const responsive = useResponsiveLayout();
  const useMobileTable = responsive.isPhone || responsive.isPhablet;
  const [stats, setStats] = useState(null);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [showSetup, setShowSetup] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sr, lr] = await Promise.all([
        fetch(`${BASE}/inbound/stats`).then((r) => r.json()),
        fetch(`${BASE}/inbound/log`).then((r) => r.json()),
      ]);
      setStats(sr.stats || null);
      setCalls(lr.calls || []);
    } catch (e) {
      setStats(null);
      setCalls([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runTest = async () => {
    setTesting(true);
    try {
      await fetch(`${BASE}/inbound/test`, { method: "POST" });
      await load();
    } catch (e) {
      // ignore
    } finally {
      setTesting(false);
    }
  };

  const badge = (outcome) => {
    if (outcome === "resolved") return <span className="vs-badge booked">Resolved</span>;
    if (outcome === "escalated") return <span className="vs-badge warm">Escalated</span>;
    if (outcome === "missed") return <span className="vs-badge closed_lost">Missed</span>;
    return <span className="vs-badge cold">{outcome}</span>;
  };

  if (loading) return <Spinner />;

  const kpis = [
    { label: "Total Today", value: fmt(stats?.total ?? 0) },
    { label: "Resolved", value: fmt(stats?.resolved ?? 0), color: "green" },
    { label: "Escalated", value: fmt(stats?.escalated ?? 0), color: "amber" },
    { label: "Avg Duration", value: `${stats?.avg_duration ?? 0}s` },
  ];

  return (
    <div>
      <div className="vs-kpi-row">
        {kpis.map((k) => (
          <div key={k.label} className={`vs-kpi ${k.color || ""}`}>
            <div className="vs-kpi-label">{k.label}</div>
            <div className="vs-kpi-value">{k.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: "var(--space-sm)", margin: "var(--space-md) 0" }}>
        <button className="vs-btn vs-btn-ghost vs-btn-sm" onClick={load} disabled={loading}>
          Refresh
        </button>
        <button className="vs-btn vs-btn-ghost vs-btn-sm" onClick={runTest} disabled={testing}>
          {testing ? "Sending..." : "Send Test Call"}
        </button>
        <button className="vs-btn vs-btn-ghost vs-btn-sm" onClick={() => setShowSetup((v) => !v)}>
          {showSetup ? "Hide Setup" : "Setup Guide"}
        </button>
      </div>

      {showSetup && (
        <div className="vs-card" style={{ marginBottom: "var(--space-md)" }}>
          <div className="vs-card-title">Telnyx + ElevenLabs Setup</div>
          <ol style={{ lineHeight: 1.8, paddingLeft: "1.2em" }}>
            <li>Sign up at telnyx.com and buy a UAE phone number.</li>
            <li>Set TELNYX_API_KEY and TELNYX_PHONE_NUMBER in .env.</li>
            <li>Create a Conversational AI agent at elevenlabs.io using the AR/EN prompts from inbound_handler.py.</li>
            <li>Select voices: Amr (Arabic) and Magic Farsi (Farsi).</li>
            <li>Configure the ElevenLabs end-of-call webhook to POST /api/voice-sales/inbound/webhook.</li>
            <li>In Telnyx, bind the SIP trunk to the ElevenLabs agent.</li>
            <li>Set ELEVENLABS_AGENT_ID and AGENT_GROUP_ID in .env.</li>
            <li>Use Send Test Call above to verify WhatsApp escalation.</li>
          </ol>
        </div>
      )}

      {useMobileTable ? (
        // M3 fix (audit): inbound calls log table is unreadable below md.
        <TableMobile
          columns={[
            { key: "caller_phone", label: "Caller", primary: true,
              format: (v) => `+${v}`,
            },
            { key: "outcome", label: "Outcome", status: true,
              format: (v) => v || "—",
            },
            { key: "date", label: "Date",
              format: (v) => v ? new Date(v).toLocaleString() : "--",
            },
            { key: "duration_seconds", label: "Duration",
              format: (v) => `${v ?? 0}s`,
            },
            { key: "summary", label: "Summary", format: (v) => v || "--" },
          ]}
          rows={calls}
          emptyText="No inbound calls logged yet."
          statusTone={(row) => {
            const o = String(row.outcome || "").toLowerCase();
            if (o.includes("escalat") || o.includes("won")) return "success";
            if (o.includes("fail") || o.includes("rejected")) return "danger";
            if (o.includes("warn")) return "warning";
            return "muted";
          }}
        />
      ) : (
        <div className="vs-table-wrap">
          <table className="vs-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Caller</th>
                <th>Duration</th>
                <th>Outcome</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {calls.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: "center", padding: "var(--space-md)" }}>
                  No inbound calls logged yet.
                </td></tr>
              ) : calls.map((c, i) => (
                <tr key={i}>
                  <td>{c.date ? new Date(c.date).toLocaleString() : "--"}</td>
                  <td>+{c.caller_phone}</td>
                  <td>{c.duration_seconds}s</td>
                  <td>{badge(c.outcome)}</td>
                  <td style={{ maxWidth: 360 }}>{c.summary || "--"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -- Main Dashboard -------------------------------------------------

const TABS = [
  { id: "overview",    label: "Overview" },
  { id: "leads",       label: "Leads" },
  { id: "call",        label: "Call" },
  { id: "inbound",     label: "Inbound" },
  { id: "demos",       label: "Demos" },
  { id: "scripts",     label: "Scripts" },
  { id: "compliance",  label: "Compliance" },
];

export default function VoiceSalesDashboard() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const r = await fetch(`${BASE}/dashboard`);
      const d = await r.json();
      setStats(d.data?.stats ?? d.data ?? null);
    } catch (e) {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  return (
    <div className="vs-page">
      <div className="vs-header">
        <div className="vs-header-left">
          <div className="vs-status-dot" />
          <div>
            <h1 className="vs-title">Voice Sales Agent</h1>
            <div className="vs-subtitle">Lessoan Protein Demo -- UAE/GCC Outbound Campaign</div>
          </div>
        </div>
        <button className="vs-btn vs-btn-ghost vs-btn-sm" onClick={loadStats} disabled={statsLoading}>
          {statsLoading ? "..." : "Refresh"}
        </button>
      </div>

      <div className="vs-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`vs-tab ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview"   && <OverviewTab stats={stats} onRefresh={loadStats} />}
      {tab === "leads"      && <LeadsTab />}
      {tab === "call"       && <CallTab />}
      {tab === "inbound"    && <InboundTab />}
      {tab === "demos"      && <DemosTab />}
      {tab === "scripts"    && <ScriptsTab />}
      {tab === "compliance" && <ComplianceTab />}

      <div style={{ textAlign: "center", marginTop: "var(--space-xl)", fontSize: "var(--font-size-xs)", color: "var(--color-muted)", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
        <img src="/brand/veridian-logo.png" alt="Veridian" style={{ width: 14, height: 14, objectFit: "contain" }} />
        Powered by Veridian
      </div>
    </div>
  );
}